import { Reel } from '../components/reel';
import { addTouchControl } from '../../helpers/touchSpinControll';
import { gameModuleStore } from '../store/gameModuleStore';
import { GAME_SPEED_TYPES } from '../../constants/gameConstants';
import { gameResourcesService } from './gameResourcesService';
import { handleSymbolAnimationPositionInMatrix } from '../../helpers/createWinLinesMatrix';
import { iconStates, specialIconKeys } from '../../constants/symbolsNames';
import {
  createCustomEases,
  handleReelIconStateInWinCase,
  handleWinLineBonePosition,
  setupPixiPlugins,
} from '../../helpers/gameModule';
import { gameConfigurationStore } from '../store/gameConfigurationStore';
import { skippablePromise } from '../../helpers/skippablePromise';
import { gameGlobalStates } from '../../constants/reelConstants';

// RU: линии 1–10 считаются слева направо (анимация `animation`), 11–20 — справа налево
//     (`animation2`, кости по барабанам в обратном порядке).
const FIRST_RIGHT_TO_LEFT_LINE = 11;
const lineAnimationName = (line) => (line >= FIRST_RIGHT_TO_LEFT_LINE ? 'animation2' : 'animation');
const RESPIN_TEXT_ANIMATION = 'animation';

class GameModuleService {
  reels = [];
  reelsContainer = null;
  winContainer = null;
  touchSpinScreen = null;
  stopNextReel = this.reelsStopGenerator();
  skippableAnimationPromise = [];
  winLineTimeOutId = null;
  // RU: номер текущего цикла показа по линиям. Новый показ (или новый спин) поднимает номер, и
  //     прежний цикл выходит на ближайшей проверке. Прежде цикл был `while (true)`: каждый
  //     выигрыш добавлял ещё один вечный цикл по той же доске.
  byLineRun = 0;

  constructor() {
    this.promiseControl = skippablePromise();
  }

  startSpin = () => {
    gameModuleStore.setGameGlobalState(gameGlobalStates.waiting);
    gameModuleStore.setResultMatrix([]);
    gameModuleStore.setCanTouchControl(false);
    clearTimeout(this.winLineTimeOutId);
    this.endAllWinAnimations();
    this.reels.forEach((reel) => {
      reel.canStop = false;
      reel.startSpin();
      reel.isForceStop = false;
    });
  };

  startRespinAnim = () => {
    this.reels.forEach((reel) => reel.startRespin());
  };

  // RU: forceStop посреди показа — досадить раскрытие и выигрышные анимации в конечный кадр
  //     (линии ускорить) и оборвать ожидание: раунд отчитается той же стадией.
  skipAnimations = () => {
    gameModuleStore.wildIcons.forEach((icon) => {
      icon.icon.goToAnimationEnd(icon.currentState);
    });
    gameModuleStore.winAnimations.forEach((winAnimation) => {
      if (winAnimation.icon) {
        winAnimation.icon.goToAnimationEnd(winAnimation.currentState);
      } else {
        winAnimation.accelerateCurrentAnimation(3);
      }
    });
    gameModuleStore.setWildIcons();
    gameModuleStore.setWinAnimations();
    this.promiseControl.skipPromise();
  };

  stopAnimation = (matrix) => {
    gameModuleStore.setResultMatrix(matrix);
    gameModuleStore.setResponseReady(true);
    if (gameModuleStore.spinSpeedType !== GAME_SPEED_TYPES.turbo) this.stopNextReel.next();
    this.reels.forEach((reel) => {
      reel.initFromResponseData(matrix);
      if (gameModuleStore.spinSpeedType === GAME_SPEED_TYPES.turbo) reel.stopTurboReelAnimation();
    });
    gameModuleStore.setSpinDirection(1);
  };

  stopRespinAnimation = (matrix) => {
    gameModuleStore.setResultMatrix(matrix);
    gameModuleStore.setResponseReady(true);
    this.reels.forEach((reel) => {
      reel.initFromResponseData(matrix);
      reel.canStop = true;
    });
  };

  // RU: барабан со звездой (респин) очередь остановки пропускает — он не крутится.
  *reelsStopGenerator() {
    let i = 0;
    while (true) {
      if (this.reels[i].respin && this.reels[i + 1]) {
        i = (i + 1) % this.reels.length;
        continue;
      }
      yield (this.reels[i].canStop = true);
      i = (i + 1) % this.reels.length;
    }
  }

  createReels = (reelsContainer, winContainer, touchSpinScreen) => {
    this.setReelsContainerAndWinContainer(reelsContainer, winContainer, touchSpinScreen);
    this.initReels();
    this.resumePendingRespin();
  };

  setReelsContainerAndWinContainer(reelsContainer, winContainer, touchSpinScreen) {
    this.reelsContainer = reelsContainer;
    this.winContainer = winContainer;
    this.touchSpinScreen = touchSpinScreen;
  }

  initReels() {
    for (let reelIndex = 0; reelIndex < gameConfigurationStore.reelsParams.length; reelIndex++) {
      const reel = new Reel({
        index: reelIndex,
        reelsContainer: this.reelsContainer,
        slotWinLayer: this.winContainer,
        isLastReel: reelIndex === gameConfigurationStore.reelsParams.length - 1,
        stopNextReel: this.stopNextReel,
        reelParams: gameConfigurationStore.reelsParams,
      });
      this.reels.push(reel);
    }
    setupPixiPlugins();
    createCustomEases();
    addTouchControl(this.touchSpinScreen, this.winContainer, this.reels, this.reelsContainer);
  }

  // RU: перезагрузка посреди серии респинов: барабаны со звездой стартовая доска уже держит
  //     (Reel.addReelIcons помечает их), выигрыш прошлого шага уже показан — сразу следующий
  //     респин. Прежде продолжение запускалось из getPropsFromFrame, до создания барабанов.
  resumePendingRespin() {
    const pending = gameModuleStore.pendingRespin;
    if (!pending) return;
    gameModuleStore.pendingRespin = null;
    gameModuleStore.retriggerMode = true;
    this.startRespin(pending.expandingStars || [], []);
  }

  // RU: шаг серии: раскрыть новые звёзды, показать выигрыш шага (если есть), попросить у ядра
  //     респин. Ожидание обрывается forceStop-ом, как и обычный показ выигрыша.
  startRespin = (reelsIndex, wins) => {
    this.skippableAnimationPromise = this.reels
      .filter((reel, i) => reelsIndex[i] !== 0 && !reel.respin)
      .map((reel) => reel.startWildAnimation());
    this.promiseControl.runPromise(this.skippableAnimationPromise, () => {
      if (wins.length) {
        const { winLines, allLinesMatrix } = handleSymbolAnimationPositionInMatrix(wins);
        this.skippableAnimationPromise = this.allWinAnimation(winLines, allLinesMatrix);
        this.promiseControl.runPromise(this.skippableAnimationPromise, this.requestRespin);
      } else {
        this.requestRespin();
      }
    });
  };

  requestRespin = () => {
    this.skippableAnimationPromise = [];
    gameModuleStore.doRespin();
    this.startReSpinTextAnimation();
  };

  startReSpinTextAnimation() {
    const text = gameResourcesService.reSpinTextScene;
    if (!text) return;
    gameModuleStore.soundService.play('ReSpinTextSound');
    text.play(RESPIN_TEXT_ANIMATION);
    text.onceAnimationEvent('complete', () => text.stop(), RESPIN_TEXT_ANIMATION);
  }

  startWinAnimation = (wins) => {
    const { winLines, allLinesMatrix } = handleSymbolAnimationPositionInMatrix(wins);
    gameModuleStore.soundService.play('WinningLineSound');
    this.skippableAnimationPromise = this.allWinAnimation(winLines, allLinesMatrix);
    this.promiseControl.runPromise(this.skippableAnimationPromise, () => {
      this.byLineWinAnimation(winLines);
      // RU: свайп поднимается ДО отчёта ядру: на этом отчёте ядро может сразу запустить
      //     следующий спин (автоигра), и его `startSpin` должен опустить флаг последним.
      gameModuleStore.setCanTouchControl(true);
      gameModuleStore.animationEnd('allLineAnimationEnd', 0);
      this.skippableAnimationPromise = [];
    });
  };

  allWinAnimation = (winLines, allLinesMatrix) => {
    const animationPromises = [];
    gameModuleStore.animationEnd('allLineAnimationStart');
    this.reels.forEach((reel) => {
      reel.startWinAnimation(handleReelIconStateInWinCase(allLinesMatrix, reel));
    });
    winLines.forEach(({ line, lines }) => {
      if (!line) return;
      const animationName = lineAnimationName(line);
      this.reels.forEach((reel) => {
        const index =
          line >= FIRST_RIGHT_TO_LEFT_LINE
            ? gameConfigurationStore.reelsParams.length - 1 - reel.index
            : reel.index;
        gameResourcesService.allWinLinesBone[line - 1][index].y = handleWinLineBonePosition(lines, reel);
      });
      const spineLine = gameResourcesService.allLinesSpine[line - 1];
      spineLine.visible = true;
      spineLine.play(animationName);
      gameModuleStore.setWinAnimations(spineLine);
      animationPromises.push(
        new Promise((animResolve) => {
          spineLine.onceAnimationEvent('complete', () => animResolve(), animationName);
        })
      );
    });
    return animationPromises;
  };

  async byLineWinAnimation(winLinesData) {
    this.byLineRun += 1;
    const run = this.byLineRun;
    let start = 0;
    while (run === this.byLineRun && gameModuleStore.gameGlobalState === gameGlobalStates.show) {
      const winLine = winLinesData[start];
      const { line, lines } = winLine;
      const isFromRight = line >= FIRST_RIGHT_TO_LEFT_LINE;
      const lineIndex = line - 1;
      const animationName = lineAnimationName(line);
      const icons = [];
      // RU: в списке — только анимации ТЕКУЩЕЙ линии (прежде он рос с каждым проходом).
      gameModuleStore.setWinAnimations();
      for (let i = 0; i < this.reels.length; i++) {
        const column = this.reels[i];
        const { visibleRows } = gameConfigurationStore.reelsParams[i];
        for (let j = 0; j < visibleRows; j++) {
          const icon = column.icons[j + visibleRows];
          icons.push(icon);
          gameModuleStore.setWinAnimations(icon);
          if (lines[j][i] === 1) {
            icon.changeState(iconStates.win);
          } else {
            icon.changeState(
              icon.iconNumber === specialIconKeys.wild ? iconStates.idle : iconStates.noWin
            );
          }
          if (lines[j][i] && line) {
            gameResourcesService.allWinLinesBone[lineIndex][i].y = handleWinLineBonePosition(
              lines,
              this.reels[isFromRight ? gameConfigurationStore.reelsParams.length - 1 - i : i]
            );
          }
        }
      }
      // RU: табло ядра показывает линию по ОБЪЕКТУ winLine (line / count / symbol / win).
      gameModuleStore.animationEnd('byLineAnimation', winLine);
      if (line) {
        const spineLine = gameResourcesService.allLinesSpine[lineIndex];
        spineLine.play(animationName);
        gameModuleStore.setWinAnimations(spineLine);
        spineLine.visible = true;
        await Promise.all([
          ...icons.map(
            (icon) =>
              new Promise((animResolve) => {
                icon.icon.onceAnimationEvent('complete', () => animResolve(), icon.currentState);
              })
          ),
          new Promise((animResolve) => {
            spineLine.onceAnimationEvent('complete', () => animResolve(), animationName);
          }),
        ]);
      } else {
        await new Promise((resolve) => {
          this.winLineTimeOutId = setTimeout(resolve, 1000);
        });
      }
      // RU: в реплее (и интро) конец первого прохода — терминал раунда, по ИНДЕКСУ записи.
      if (
        run === this.byLineRun &&
        (gameConfigurationStore.isReplay || gameModuleStore.isIntro) &&
        start === winLinesData.length - 1
      ) {
        gameModuleStore.animationEnd('byLineAnimationEnd');
      }
      start = (start + 1) % winLinesData.length;
    }
  }

  endAllWinAnimations = () => {
    this.byLineRun += 1;
    gameModuleStore.winAnimations.forEach((animation) => {
      if (animation.icon) {
        animation.icon.goToAnimationEnd(animation.currentState);
      } else {
        animation.goToAnimationEnd(animation.state.tracks[0]?.animation.name);
      }
    });
    gameModuleStore.setWinAnimations();
  };

  clearRetriggerState = () => {
    this.reels.forEach((reel) => (reel.respin = false));
  };
}

export const gameModuleService = new GameModuleService();
